export class Recipe {
  constructor({
    ingredients,
    ingredientOptions,
    servingTemp = -18,
    target_hardness = 75,
    overrun = 30,
    evaporationPercentage = 0,
    includeAlcoholInEvaporation = true,
    categories = [],
    categoryId = '',
    notes = '',
    name = ''
  }) {
    this.ingredients = ingredients || [];
    this.ingredientOptions = ingredientOptions || [];
    this.servingTemp = servingTemp;
    this.target_hardness = target_hardness;
    this.overrun = overrun;
    this.evaporationPercentage = evaporationPercentage;
    this.includeAlcoholInEvaporation = includeAlcoholInEvaporation;
    this.categories = categories;
    this.categoryId = categoryId;
    this.notes = notes;
    this.name = name;
  }

  isIngredientInMix(ingredient) {
    if (!ingredient) return false;
    const explicit = ingredient.in_mix;
    if (typeof explicit !== 'undefined' && explicit !== null) {
      if (typeof explicit === 'string') {
        const normalized = explicit.toString().toLowerCase();
        if (['0', 'false', 'no', 'off'].includes(normalized)) {
          return false;
        }
        if (['1', 'true', 'yes', 'on'].includes(normalized)) {
          return true;
        }
      } else if (typeof explicit === 'number') {
        return explicit === 1;
      } else {
        return !!explicit;
      }
    }

    const ingData = this.ingredientOptions.find(opt => ingredient.id && opt.id === ingredient.id);
    if (ingData && ingData.can_be_mix === false) {
      return false;
    }

    return true;
  }

  getTotalWeight() {
    return this.ingredients.reduce((acc, ing) => acc + (Number(ing.amount) || 0), 0);
  }

  // Peso total solo de ingredientes de mezcla (sin inclusiones)
  getMixWeight() {
    return this.ingredients.reduce((acc, ing) => {
      if (!this.isIngredientInMix(ing)) {
        return acc;
      }
      return acc + (Number(ing.amount) || 0);
    }, 0);
  }

  // Peso total solo de inclusiones
  getInclusionsWeight() {
    return this.ingredients.reduce((acc, ing) => {
      if (this.isIngredientInMix(ing)) {
        return acc;
      }
      return acc + (Number(ing.amount) || 0);
    }, 0);
  }

  getTotalWeightAdjusted() {
    const mixWeight = this.getMixWeight();
    const inclusionsWeight = this.getInclusionsWeight();
    if (!mixWeight) return inclusionsWeight;
    const { totalEvaporated } = this.getEvaporationAmounts();
    const adjustmentFactor = 1 - totalEvaporated / 100;
    return (mixWeight * adjustmentFactor) + inclusionsWeight;
  }

  getIngredientAmount(prop, percentage) {
    const total = this.getMixWeight(); // Usar solo peso de mezcla
    if (typeof percentage === 'undefined') {
      percentage = true;
    }
    if (!total) return 0;
    let divisor = 1;
    if (percentage) {
        divisor = total / 100;
    }
    return (
      this.ingredients.reduce((acc, ing) => {
        if (!this.isIngredientInMix(ing)) {
          return acc;
        }
        const ingData = this.ingredientOptions.find(opt => ing.id && opt.id === ing.id);
        const qty = Number(ing.amount) || 0;
        return acc + (ingData ? (qty * (ingData[prop] || 0) / 100) : 0);
      }, 0) / divisor );
  }

  getCurrentValue(prop, percentage) {
    if (typeof prop === 'string') {
      prop = { parameter: prop };
    }
    if (typeof percentage === 'undefined') {
      percentage = true;
    }
    const total = this.getMixWeight(); // Usar solo peso de mezcla
    if (this.ingredientOptions.length === 0 || total === 0) return 0;
    switch (prop.parameter) {
      case 'fat': return this.getIngredientAmount('fats', percentage);
      case 'milk_fat':
      case 'milkfat': return this.getIngredientAmount('milkfat', percentage);
      case 'lactose': return this.getIngredientAmount('lactose', percentage);
      case 'lactose_water_ratio': return this.getIngredientAmount('lactose', percentage) / this.getIngredientAmount('water', percentage) * 100;
      case 'msnf': return this.getIngredientAmount('msnf', percentage);
      case 'pod': return this.getIngredientAmount('pod', percentage);
      case 'stabilizer': return this.getIngredientAmount('stabilizer', percentage);
      case 'stabilizer_water_ratio': return this.getIngredientAmount('stabilizer', false) / this.getIngredientAmount('water', false) * 100;
      case 'emulsifiers': return this.getIngredientAmount('emulsifiers', percentage);
      case 'total_solids': return this.getIngredientAmount('total_solids', percentage);
      case 'water': return this.getIngredientAmount('water', percentage);
      case 'pac': return this.getIngredientAmount('pac', percentage);
      case 'pacn': return this.getIngredientAmount('pac') / this.getIngredientAmount('water') * 100;
      case 'hf':
      case 'hardening_factor': return this.getIngredientAmount('hardening_factor', percentage);
      case 'alcohol': return this.getIngredientAmount('alcohol', percentage);
      default: return this.getIngredientAmount(prop.parameter, percentage);
    }
  }

  static getScale(prop, min) {
    switch (prop.parameter) {
      case 'fat': return min ? 0 : (prop.max < 5 ? 7 : 25);
      case 'milk_fat':
      case 'milkfat': return min ? 0 : 20;
      case 'lactose': return min ? 0 : 15;
      case 'lactose_water_ratio': return min ? 0 : 15;
      case 'msnf': return min ? 0 : 20;
      case 'pod': return min ? 0 : 40;
      case 'stabilizer': return min ? 0 : 2;
      case 'stabilizer_water_ratio': return min ? 0 : 1;
      case 'emulsifiers': return min ? 0 : 2;
      case 'total_solids': return min ? 20 : 50;
      case 'water': return min ? 0 : 100;
      case 'pac':
      case 'pacn': return min ? 0 : 50;
      case 'alcohol': return min ? 0 : 5;
      default: return false;
    }
  }

  getXw() {
    return 1 - this.getAdjustedValue('total_solids') / 100; // Alcohol?
  }

  getFusionPoint() {
    return this.getFusionPointFromPAC(this.getAdjustedValue('pac'), this.getXw(), this.getAdjustedValue('msnf'));
  }

  getFusionPointFromPAC(pac, Xw, msnf) {
    const table = [
      [0,0],[3,0.18],[4,0.24],[5,0.29],[6,0.35],[7,0.41],[8,0.47],[9,0.53],[10,0.59],[11,0.66],[12,0.72],[13,0.78],[14,0.84],[15,0.90],[16,0.97],[17,1.03],[18,1.10],[19,1.16],[20,1.23],[21,1.29],[22,1.35],[23,1.41],[24,1.47],[25,1.54],[26,1.60],[27,1.67],[28,1.73],[29,1.80],[30,1.86],[31,1.92],[32,1.97],[33,2.03],[34,2.09],[35,2.15],[36,2.21],[37,2.27],[38,2.34],[39,2.40],[40,2.47],[41,2.53],[42,2.60],[43,2.66],[44,2.72],[45,2.78],[46,2.85],[47,2.92],[48,2.99],[49,3.06],[50,3.13],[51,3.20],[52,3.27],[53,3.35],[54,3.42],[55,3.49],[56,3.56],[57,3.63],[58,3.70],[59,3.78],[60,3.85],[61,3.93],[62,4.02],[63,4.10],[64,4.18],[65,4.25],[66,4.33],[67,4.40],[68,4.47],[69,4.54],[70,4.62],[71,4.69],[72,4.77],[73,4.85],[74,4.92],[75,5.00],[76,5.09],[77,5.17],[78,5.26],[79,5.35],[80,5.44],[81,5.53],[82,5.61],[83,5.69],[84,5.77],[85,5.84],[86,5.92],[87,5.99],[88,6.07],[89,6.15],[90,6.23],[91,6.32],[92,6.41],[93,6.50],[94,6.60],[95,6.70],[96,6.80],[97,6.88],[98,6.96],[99,7.04],[100,7.13],[101,7.23],[102,7.32],[103,7.40],[104,7.48],[105,7.56],[106,7.64],[107,7.72],[108,7.80],[109,7.88],[110,7.96],[111,8.04],[112,8.14],[113,8.23],[114,8.33],[115,8.43],[116,8.52],[117,8.62],[118,8.72],[119,8.82],[120,8.92],[121,9.01],[122,9.10],[123,9.19],[124,9.28],[125,9.36],[126,9.45],[127,9.54],[128,9.62],[129,9.71],[130,9.79],[131,9.88],[132,9.96],[133,10.05],[134,10.13],[135,10.22],[136,10.30],[137,10.39],[138,10.47],[139,10.55],[140,10.64],[141,10.72],[142,10.80],[143,10.89],[144,10.97],[145,11.04],[146,11.12],[147,11.19],[148,11.26],[149,11.34],[150,11.41],[151,11.48],[152,11.56],[153,11.63],[154,11.71],[155,11.80],[156,11.88],[157,11.97],[158,12.05],[159,12.14],[160,12.23],[161,12.31],[162,12.40],[163,12.49],[164,12.58],[165,12.67],[166,12.74],[167,12.81],[168,12.88],[169,12.95],[170,13.01],[171,13.08],[172,13.15],[173,13.21],[174,13.28],[175,13.35],[176,13.41],[177,13.48],[178,13.55],[179,13.61],[180,13.68]
    ];

    if (Xw <= 0) return 0;
    const pacn = Math.round(pac / Xw);
    if (pacn < 0 ) return 0;

    let FPG_Salt = 0;
    if (msnf) {
      FPG_Salt = msnf * -2.37 / (Xw * 100);
    }

    if (pacn > 180) {
      return (-1 * (pacn * 0.08428 - 1.266) + FPG_Salt);
    }

    for (let i = 1; i < table.length; i++) {
      const [p0, f0] = table[i - 1];
      const [p1, f1] = table[i];
      if (pacn >= p0 && pacn <= p1) {
        return ((f0 + ((f1 - f0) * (pacn - p0)) / (p1 - p0)) * -1 + FPG_Salt);
      }
    }
    return null;
  }

  getHardnessIteratingInServingTemp() {
    // Usar valores ajustados si hay evaporación
    var initialXw = this.getXw();
    var pac = this.getAdjustedValue('pac');
    var msnf = this.getAdjustedValue('msnf');

    var hf = this.getAdjustedValue('hardening_factor');
    var pac_hf = pac - hf;

    var iniTemp = this.getFusionPointFromPAC(pac, initialXw, msnf);
    if (iniTemp <= this.servingTemp) {
      return 0;
    }

    var lastT = 0;
    var step = 0.5;
    for (let t = 0; t < 100; t += step) {
      var temp = this.getFusionPointFromPAC(pac_hf, initialXw * (1-t/100), msnf);
      var temp2 = this.getFusionPointFromPAC(pac, initialXw * (1-t/100), msnf);
      var temp_avg = (temp + temp2) / 2;
      if (temp_avg <= this.servingTemp) {
          return lastT + (t - lastT) * (step) / (temp_avg - lastT);
      }
      lastT = t;
    }

    return 100;
  }

  getWfIteratingInServingTemp() {
    // Usar valores ajustados si hay evaporación
    var initialXw = this.getXw();
    var pac = this.getAdjustedValue('pac');
    var msnf = this.getAdjustedValue('msnf');

    var iniTemp = this.getFusionPointFromPAC(pac, initialXw, msnf);
    if (iniTemp <= this.servingTemp) {
      return 0;
    }

    var lastT = 0;
    var step = 0.5;
    for (let t = 0; t < 100; t += step) {
      var temp = this.getFusionPointFromPAC(pac, initialXw * (1-t/100), msnf);
      if (temp <= this.servingTemp) {
          return lastT + (t - lastT) * (step) / (temp - lastT);
      }
      lastT = t;
    }

    return 100;
  }

  getGraphData() {
    var series = [];
    var initialXw = this.getXw();
    var pac = this.getAdjustedValue('pac');
    var msnf = this.getAdjustedValue('msnf');

    var hf = this.getAdjustedValue('hardening_factor');
    var pac_hf = pac - hf;

    var data_wf = [];
    var data_hf = [];
    var data_hf_av = [];
    for (let t = 0; t <= 90; t += 1) {
        var _t = this.getFusionPointFromPAC(pac, initialXw * (1-t/100), msnf);
        var _thf = this.getFusionPointFromPAC(pac_hf, initialXw * (1-t/100), msnf);
        if (_t > -35) {
            data_wf.push({ ice: t, temp: _t.toFixed(2) });
        }
        if (_thf > -35) {
            data_hf.push({ ice: t, temp: _thf.toFixed(2) });
        }
        const _t_av = (_t + _thf) / 2;
        if (_t_av > -35) {
            data_hf_av.push({ ice: t, temp: _t_av.toFixed(2) });
        }
    }
    series.push({
      name: '%WF',
      data: data_wf,
        color: '#1976d2'
    });
    if (hf > 0) {
        if (data_hf.length > 0){
            series.push({
                name: '%WF (HF)',
                data: data_hf,
                color: '#aaa'
            });
        }
        if (data_hf_av.length > 0) {
            series.push({
                name: '%WF+HF Average',
                data: data_hf_av,
                color: '#aa7f50'
            });
        }
    }

    
    /*
    var data2 = [];
    const fpd = this.getFusionPointFromPAC(pac, initialXw, msnf);
    for (let t = fpd; t > -30; t -= 1) {
      data2.push({ temp: t, ice: Math.max(0, this.getWf(t)) });
    }
    series.push({
      name: '%WF (Tchigeov)',
      data: data2
    });*/

    return series;
  }

  getWf(t) {
    const Xw =  this.getXw();
    const fpd = this.getFusionPointFromPAC(this.getAdjustedValue('pac'), Xw);
    if (fpd === null || t > fpd) return 0;
    return ((1.105 / ( 1 + ( 0.7138 / ( Math.log(fpd - t + 1) ) ) ) ) * 100).toFixed(2);
  }

  getWeightHuman(weight) {
    if (weight === 0) return '0 g';
    if (weight < 1000) return `${Math.round(weight)} g`;
    if (weight < 10000) return `${(weight / 1000).toFixed(2)} kg`;
    if (weight < 100000) return `${(weight / 1000).toFixed(1)} kg`;
    if (weight < 1000000) return `${Math.round(weight / 1000)} kg`;
    return `${Math.round(weight / 1000000)} t`;
  }

  getScaledIngredientsText(copyGrams = 1000) {
    const total = this.getTotalWeight();
    if (!total || !this.ingredients.length) return '';
    return this.ingredients
      .filter(ing => ing.id && ing.amount)
      .map(ing => {
        const ingData = this.ingredientOptions.find(opt => opt.id === ing.id);
        const name = ingData ? ingData.name : ing.id;
        const scaledAmount = (Number(ing.amount) * copyGrams / total).toFixed(2);
        return `${name}: ${scaledAmount} g`;
      })
      .join('\n');
  }

  getEvaporationAmounts() {
  const evaporationPercentage = this.evaporationPercentage || 0;
  if (evaporationPercentage === 0) {
    return { waterEvaporated: 0, alcoholEvaporated: 0, totalEvaporated: 0 };
  }

  const alcoholContent = this.getCurrentValue('alcohol'); // g de alcohol
  const waterContent = this.getCurrentValue('water');     // g de agua
  const totalLiquid = alcoholContent + waterContent;

  if (totalLiquid === 0) {
    return { waterEvaporated: 0, alcoholEvaporated: 0, totalEvaporated: 0 };
  }

  // Evaporación máxima en gramos (base 100 g mezcla)
  const maxEvaporation = Math.min(evaporationPercentage, totalLiquid);

  // Caso: solo agua se evapora
  if (!this.includeAlcoholInEvaporation) {
    const waterEvaporated = Math.min(maxEvaporation, waterContent);
    return { waterEvaporated, alcoholEvaporated: 0, totalEvaporated: waterEvaporated };
  }

  // --- Distribución con fugacidad ---
  const alcoholFugacityFactor = 2.5; // configurable
  const alcoholWeight = alcoholContent * alcoholFugacityFactor;
  const waterWeight = waterContent;
  const totalWeight = alcoholWeight + waterWeight;

  // Evaporación teórica según pesos relativos
  let waterEvaporated = (waterWeight / totalWeight) * maxEvaporation;
  let alcoholEvaporated = (alcoholWeight / totalWeight) * maxEvaporation;

  // Limitar a lo disponible
  waterEvaporated = Math.min(waterEvaporated, waterContent);
  alcoholEvaporated = Math.min(alcoholEvaporated, alcoholContent);

  // Ver si quedó sobrante por limitación
  let totalActual = waterEvaporated + alcoholEvaporated;
  let remaining = maxEvaporation - totalActual;

  // Redistribución simple del sobrante
  if (remaining > 0) {
    if (waterEvaporated < waterContent) {
      const addWater = Math.min(remaining, waterContent - waterEvaporated);
      waterEvaporated += addWater;
      remaining -= addWater;
    }
    if (remaining > 0 && alcoholEvaporated < alcoholContent) {
      const addAlcohol = Math.min(remaining, alcoholContent - alcoholEvaporated);
      alcoholEvaporated += addAlcohol;
      remaining -= addAlcohol;
    }
  }

  return {
    waterEvaporated,
    alcoholEvaporated,
    totalEvaporated: waterEvaporated + alcoholEvaporated
  };
}

  // Calcula las cantidades absolutas evaporadas (en gramos sobre 100g total)
  getEvaporationAmountsOld() {
    if (!this.evaporationPercentage || this.evaporationPercentage === 0) {
      return { waterEvaporated: 0, alcoholEvaporated: 0, totalEvaporated: 0 };
    }
    
    const alcoholContent = this.getCurrentValue('alcohol'); // % de alcohol
    const waterContent = this.getCurrentValue('water'); // % de agua
    const totalLiquid = waterContent + alcoholContent;
    
    // Limitar la evaporación al total de líquido disponible
    const maxEvaporation = Math.min(this.evaporationPercentage, totalLiquid);
    
    // Si el alcohol no se incluye en la evaporación, solo se evapora agua
    if (!this.includeAlcoholInEvaporation) {
      const waterEvaporated = Math.min(maxEvaporation, waterContent);
      return { waterEvaporated, alcoholEvaporated: 0, totalEvaporated: waterEvaporated };
    }
    
    // Si no hay líquido, no hay evaporación
    if (totalLiquid === 0) {
      return { waterEvaporated: 0, alcoholEvaporated: 0, totalEvaporated: 0 };
    }
    
    // Factor de fugacidad del alcohol (el alcohol se evapora ~2/3x más rápido que el agua)
    const alcoholFugacityFactor = 2.5;
    
    // Calculamos las proporciones relativas considerando la fugacidad
    const alcoholWeight = alcoholContent * alcoholFugacityFactor;
    const waterWeight = waterContent * 1.0;
    const totalWeight = alcoholWeight + waterWeight;
    
    // Distribuimos la evaporación máxima según las proporciones ajustadas
    const waterEvaporationShare = (waterWeight / totalWeight) * maxEvaporation;
    const alcoholEvaporationShare = (alcoholWeight / totalWeight) * maxEvaporation;
    
    // Limitamos la evaporación a lo que realmente hay disponible de cada componente
    const actualWaterEvaporated = Math.min(waterEvaporationShare, waterContent);
    const actualAlcoholEvaporated = Math.min(alcoholEvaporationShare, alcoholContent);
    
    // Si después de limitar por disponibilidad, no hemos alcanzado la evaporación deseada,
    // redistribuimos el exceso al otro componente
    const totalActualEvaporated = actualWaterEvaporated + actualAlcoholEvaporated;
    const remainingEvaporation = maxEvaporation - totalActualEvaporated;
    
    if (remainingEvaporation > 0) {
      // Si hay agua disponible y no hemos evaporado todo el alcohol posible
      if (actualWaterEvaporated < waterContent && actualAlcoholEvaporated >= alcoholContent) {
        const additionalWater = Math.min(remainingEvaporation, waterContent - actualWaterEvaporated);
        return { 
          waterEvaporated: actualWaterEvaporated + additionalWater, 
          alcoholEvaporated: actualAlcoholEvaporated,
          totalEvaporated: actualWaterEvaporated + additionalWater
        };
      }
      // Si hay alcohol disponible y no hemos evaporado toda el agua posible
      else if (actualAlcoholEvaporated < alcoholContent && actualWaterEvaporated >= waterContent) {
        const additionalAlcohol = Math.min(remainingEvaporation, alcoholContent - actualAlcoholEvaporated);
        return { 
          waterEvaporated: actualWaterEvaporated, 
          alcoholEvaporated: actualAlcoholEvaporated + additionalAlcohol,
          totalEvaporated: actualWaterEvaporated + actualAlcoholEvaporated + additionalAlcohol
        };
      }
    }
    
    return { 
      waterEvaporated: actualWaterEvaporated, 
      alcoholEvaporated: actualAlcoholEvaporated,
      totalEvaporated: actualWaterEvaporated + actualAlcoholEvaporated
    };
  }

  // Calcula la cantidad de agua evaporada (para mostrar en la tabla)
  getEvaporatedWaterAmount() {
    const { waterEvaporated } = this.getEvaporationAmounts();
    return waterEvaporated;
  }

  // Calcula la cantidad de alcohol evaporado (para mostrar en la tabla)
  getEvaporatedAlcoholAmount() {
    const { alcoholEvaporated } = this.getEvaporationAmounts();
    return alcoholEvaporated;
  }

  // Calcula el PAC perdido por evaporación de alcohol
  getPacLostFromAlcohol() {
    const originalAlcohol = this.getCurrentValue('alcohol');
    const adjustedAlcohol = this.getAdjustedValue('alcohol');
    const alcoholEvaporated = originalAlcohol - adjustedAlcohol;
    const alcoholPacFactor = 7.4; // Factor PAC del alcohol
    
    return alcoholEvaporated * alcoholPacFactor;
  }

getAdjustedValue(prop, percentage = true) {
  if (typeof prop === 'string') prop = { parameter: prop };

  const evap = this.getEvaporationAmounts();
  const factor = this.getTotalWeight() / 100;

  // Escalar evaporación al total real
  const waterEvaporated   = evap.waterEvaporated   * factor;
  const alcoholEvaporated = evap.alcoholEvaporated * factor;
  const totalEvaporated   = evap.totalEvaporated   * factor;

  const originalTotal = this.getTotalWeight();
  const newTotal = originalTotal - totalEvaporated;

  if (totalEvaporated === 0) {
    return this.getCurrentValue(prop, percentage);
  }

  switch (prop.parameter) {
    case 'water': {
      const water0 = this.getCurrentValue('water', false);
      const water_g = Math.max(0, water0 - waterEvaporated);
      return percentage ? (water_g / newTotal * 100) : water_g;
    }

    case 'alcohol': {
      const alc0 = this.getCurrentValue('alcohol', false);
      const alc_g = Math.max(0, alc0 - alcoholEvaporated);
      return percentage ? (alc_g / newTotal * 100) : alc_g;
    }

    case 'pac':
    case 'pacn': {
      const F_ALC = 7.4;

      const alc0 = this.getCurrentValue('alcohol', false); 
      const alc_g = Math.max(0, alc0 - alcoholEvaporated);

      const pac0_abs = this.getCurrentValue('pac', false); 
      const pacOtros_abs = Math.max(0, pac0_abs - alc0 * F_ALC);

      const pac_abs = pacOtros_abs + alc_g * F_ALC;

      let pacOut = percentage ? (pac_abs / newTotal * 100) : pac_abs;

      if (prop.parameter === 'pacn') {
        const xw = this.getXw();
        if (xw <= 0) return Infinity;
        pacOut = pacOut / xw * 100;
      }

      return pacOut;
    }

    case 'stabilizer_water_ratio':
    case 'lactose_water_ratio': {
      const water0 = this.getCurrentValue('water', false);
      const water_g = Math.max(0, water0 - waterEvaporated);
      const originalValue = this.getCurrentValue(prop, false);
      if (water_g === 0) return 100;
      return originalValue * (water0 / water_g);
    }

    default: {
      const abs0 = this.getCurrentValue(prop, false);
      return percentage ? (abs0 / newTotal * 100) : abs0;
    }
  }
}

  // Calcula los valores ajustados después de la evaporación
  getAdjustedValueOld(prop, percentage) {
    if (typeof prop === 'string') {
      prop = { parameter: prop };
    }
    if (typeof percentage === 'undefined') {
      percentage = true;
    }
    
    const originalValue = this.getCurrentValue(prop, percentage);
    const { waterEvaporated, alcoholEvaporated, totalEvaporated } = this.getEvaporationAmounts();
    
    // Si no hay evaporación, devolver valor original
    if (totalEvaporated === 0) return originalValue;
    
    // Calculamos la nueva masa total después de la evaporación
    const originalTotal = 100; // Trabajamos con base 100
    const newTotal = originalTotal - totalEvaporated;
    
    switch (prop.parameter) {
      case 'water': {
        const originalWaterPercent = this.getCurrentValue('water', true); // % de agua
        const remainingWaterPercent = Math.max(0, originalWaterPercent - waterEvaporated);
        return percentage ? (remainingWaterPercent / newTotal * 100) : remainingWaterPercent;
      }
      case 'alcohol': {
        const originalAlcoholPercent = this.getCurrentValue('alcohol', true); // % de alcohol
        const remainingAlcoholPercent = Math.max(0, originalAlcoholPercent - alcoholEvaporated);
        return percentage ? (remainingAlcoholPercent / newTotal * 100) : remainingAlcoholPercent;
      }
      case 'pac':
      case 'pacn':
        // PAC se ve afectado por la evaporación del alcohol y la concentración
        const originalAlcoholPercent = this.getCurrentValue('alcohol', true);
        const remainingAlcoholPercent = Math.max(0, originalAlcoholPercent - alcoholEvaporated);
        const alcoholPacFactor = 7.4; // Factor PAC del alcohol
        
        const originalValueAbsolute = this.getCurrentValue(prop, false);
        const originalTotalFactor = this.getCurrentValue('total', false) / 100;

        // El PAC base (sin alcohol) se mantiene constante en términos absolutos
        const pacFromOtherSources = Math.max(0, originalValueAbsolute - originalTotalFactor * (originalAlcoholPercent * alcoholPacFactor));
        const remainingAlcoholPac = remainingAlcoholPercent * alcoholPacFactor * newTotal;

        const totalPac = pacFromOtherSources + remainingAlcoholPac;

        const pac = percentage ? (totalPac / newTotal) : totalPac;
        if ('pacn' === prop.parameter) {
          return pac / this.getXw() * 100;
        }
        return pac;
      // water_ratios
      case 'stabilizer_water_ratio':
      case 'lactose_water_ratio':
          const originalWaterPercent = this.getCurrentValue('water', true); // % de agua
          const remainingWaterPercent = Math.max(0, originalWaterPercent - waterEvaporated);
          if (remainingWaterPercent === 0) {
            return 100;
          }
          return originalValue * (originalWaterPercent / remainingWaterPercent);
      case 'total_solids':
      case 'fat':
      case 'milkfat':
      case 'msnf':
      case 'pod':
      case 'lactose':
      case 'stabilizer':
      case 'emulsifiers':
        // Todos los demás componentes se mantienen constantes en valor absoluto
        return originalValue * originalTotal / newTotal;
      default:
        return originalValue * originalTotal / newTotal;
    }
  }
}
